/**
 * Turning engine state into the one thing the player should do next.
 *
 * UI_UX section 7 asks the centre strip for "the only verb the player needs
 * now". That verb is derived here, from `legalActions` and the phase, so the
 * button and the battlefield can never disagree about what is possible.
 *
 * Nothing in this file branches on a card name or id.
 */

import {
  COMBAT_STEP_ORDER,
  hasPlayableResponse,
  profile,
  type Action,
  type GameState,
  type Phase,
  type PendingChoice,
} from '@/engine';

export type PrimaryIntent =
  | { kind: 'action'; action: Action }
  /** The player must finish a declaration before the button does anything. */
  | { kind: 'declareAttackers' }
  | { kind: 'confirmBlocks' }
  | { kind: 'waiting' };

export interface PrimaryVerb {
  label: string;
  intent: PrimaryIntent;
  disabled: boolean;
  /** One short line under the button. Explains, never scolds. */
  hint?: string;
  tone: 'primary' | 'secondary';
}

export interface InteractionInputs {
  state: GameState;
  legal: Action[];
  localPlayer: string;
  /** True while the player is mid-declaration in the battlefield. */
  declaringAttackers: boolean;
  declaringBlockers: boolean;
  /** How many attackers or blockers the player has picked so far. */
  pickedCount: number;
  botThinking: boolean;
}

const PHASE_LABEL: Record<Phase, string> = {
  setup: 'Setup',
  sync: 'Sync',
  draw: 'Draw',
  main1: 'Main 1',
  combat: 'Combat',
  main2: 'Main 2',
  end: 'End',
  over: 'Over',
};

export function phaseLabel(phase: Phase): string {
  return PHASE_LABEL[phase] ?? phase;
}

export const COMBAT_STEP_LABEL: Record<string, string> = {
  declareAttackers: 'Declare attackers',
  attackersDeclared: 'Attackers declared',
  declareBlockers: 'Declare blockers',
  blockersDeclared: 'Blocks declared',
  frontrunDamage: 'Frontrun damage',
  regularDamage: 'Damage',
  combatEnd: 'End of combat',
};

function find(legal: Action[], type: Action['type']): Action | undefined {
  return legal.find((a) => a.type === type);
}

/** The pending choice the local player owes the engine, if any. */
export function localPendingChoice(state: GameState, localPlayer: string): PendingChoice | undefined {
  return state.pendingChoices.find((c) => c.player === localPlayer);
}

/**
 * The single verb for the primary action button.
 *
 * Order matters: an owed choice beats everything, then a declaration in
 * progress, then a response window, then the ordinary phase advance.
 */
export function primaryVerb(input: InteractionInputs): PrimaryVerb {
  const { state, legal, localPlayer, declaringAttackers, declaringBlockers, pickedCount, botThinking } = input;

  if (state.phase === 'over') {
    return { label: 'Game over', intent: { kind: 'waiting' }, disabled: true, tone: 'secondary' };
  }

  if (localPendingChoice(state, localPlayer)) {
    return {
      label: 'Answer the prompt',
      intent: { kind: 'waiting' },
      disabled: true,
      hint: 'The game is waiting on your choice.',
      tone: 'secondary',
    };
  }

  // Setup: keep or redraw the opening hand.
  if (state.phase === 'setup') {
    const keep = find(legal, 'KeepHand');
    if (keep) {
      return {
        label: 'Keep hand',
        intent: { kind: 'action', action: keep },
        disabled: false,
        hint: 'Or redraw once for six cards.',
        tone: 'primary',
      };
    }
    return {
      label: 'Waiting',
      intent: { kind: 'waiting' },
      disabled: true,
      hint: 'Waiting for your opponent.',
      tone: 'secondary',
    };
  }

  // A declaration the player started in the battlefield.
  if (declaringAttackers) {
    return {
      label: pickedCount > 0 ? `Attack with ${pickedCount}` : 'Attack with nobody',
      intent: { kind: 'declareAttackers' },
      disabled: false,
      hint: 'Click your characters to send them in.',
      tone: 'primary',
    };
  }
  if (declaringBlockers) {
    return {
      label: pickedCount > 0 ? `Confirm ${pickedCount} block${pickedCount === 1 ? '' : 's'}` : 'Take it',
      intent: { kind: 'confirmBlocks' },
      disabled: false,
      hint: 'Click a blocker, then the attacker it stops.',
      tone: 'primary',
    };
  }

  if (legal.length === 0) {
    return {
      label: botThinking ? 'Opponent thinking' : 'Waiting',
      intent: { kind: 'waiting' },
      disabled: true,
      hint: botThinking ? undefined : 'Waiting for your opponent.',
      tone: 'secondary',
    };
  }

  // The stack is busy, or a response window is open.
  const pass = find(legal, 'Pass');
  if (state.stack.length > 0 && pass) {
    const canRespond = hasPlayableResponse(state, localPlayer);
    return {
      label: canRespond ? 'Pass' : 'Resolve',
      intent: { kind: 'action', action: pass },
      disabled: false,
      hint: canRespond ? 'Or play an Exploit in response.' : 'Let the top of the Stack resolve.',
      tone: 'primary',
    };
  }

  // Combat verbs.
  if (state.phase === 'combat' && state.activePlayer === localPlayer) {
    if (state.combat.step === 'declareAttackers' && find(legal, 'DeclareAttackers')) {
      return {
        label: 'Declare attackers',
        intent: { kind: 'declareAttackers' },
        disabled: false,
        hint: 'Pick who attacks, then confirm.',
        tone: 'primary',
      };
    }
  }
  if (state.phase === 'combat' && state.activePlayer !== localPlayer && find(legal, 'DeclareBlockers')) {
    return {
      label: 'Declare blockers',
      intent: { kind: 'confirmBlocks' },
      disabled: false,
      hint: 'Pick who blocks, then confirm.',
      tone: 'primary',
    };
  }

  const next = find(legal, 'NextPhase');
  if (next) {
    return {
      label: nextPhaseLabel(state),
      intent: { kind: 'action', action: next },
      disabled: false,
      hint: nextPhaseHint(state, localPlayer),
      tone: 'primary',
    };
  }

  if (pass) {
    return {
      label: 'Pass',
      intent: { kind: 'action', action: pass },
      disabled: false,
      tone: 'primary',
    };
  }

  return { label: 'Waiting', intent: { kind: 'waiting' }, disabled: true, tone: 'secondary' };
}

function nextPhaseLabel(state: GameState): string {
  switch (state.phase) {
    case 'main1':
      return 'Go to combat';
    case 'combat':
      return 'End combat';
    case 'main2':
      return 'End turn';
    case 'end':
      return 'End turn';
    default:
      return 'Next phase';
  }
}

/**
 * The one hint UI_UX asks for by name: a player who has not installed a Wallet
 * this turn is told so before they end their main phase.
 */
function nextPhaseHint(state: GameState, localPlayer: string): string | undefined {
  const p = state.players[localPlayer];
  if (!p) return undefined;
  if (state.phase !== 'main1' && state.phase !== 'main2') return undefined;
  if (state.activePlayer !== localPlayer) return undefined;
  if (p.walletsInstalledThisTurn > 0) return undefined;
  const hasWallet = p.hand.some((id) => isWalletInHand(state, id));
  return hasWallet ? 'You have not installed a Wallet this turn.' : undefined;
}

function isWalletInHand(state: GameState, instanceId: string): boolean {
  return profile(state, instanceId).type === 'wallet';
}

/** Breadcrumb pills for the centre strip. */
export function breadcrumbs(state: GameState, localPlayer: string): string[] {
  const crumbs = [`Turn ${state.turn}`];
  crumbs.push(state.activePlayer === localPlayer ? 'Your turn' : 'Opponent turn');
  crumbs.push(phaseLabel(state.phase));
  if (state.phase === 'combat') {
    const label = COMBAT_STEP_LABEL[state.combat.step];
    if (label) crumbs.push(label);
  }
  return crumbs;
}

export { COMBAT_STEP_ORDER };
